import React, { useState, useEffect, useRef } from 'react';
import ShowCard from '../../../Components/ShowCard/ShowCard';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper';
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import 'swiper/swiper-bundle.min.css';
import { useWindowWidth } from '@react-hook/window-size';
import { ReactComponent as RightArrow } from "../../../assets/Icons/pageDownArrow.svg";
import Translate from '../../../Components/MultiLanguage/Translate';
import { rowItemCount, translateText } from "../../../utils/utils";
import { useTranslation } from 'react-i18next';

const MoreTopics = ({ data }) => {
  const { t } = useTranslation();
  const dataArray = data?.shows;
  const projectInfo = useSelector((state) => state?.projectInfo?.value);
  const isRTL = useSelector((state) => state?.rtl?.isRTL);
  const languageModal = useSelector((state) => state?.languageModal?.value);
  const [activeTopic, setActiveTopic] = useState(dataArray?.[0]?.category_name || '');
  const swiperRef = useRef(null);
  const topicsSwiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const cardCounts = dataArray?.reduce((acc, item) => {
    acc[item.category_name] = item.shows?.length || 0;
    return acc;
  }, {});
  const [activeTopicData, setActiveTopicData] = useState(dataArray?.find(item => item.category_name === activeTopic));
  const [numCards, setNumCards] = useState(cardCounts?.[activeTopic] || 0);
  const windowSize = useWindowWidth();

  const thumbnailOrientation = data?.shows?.thumbnail_orientation ? data?.shows?.thumbnail_orientation : projectInfo?.projectConfig?.config?.THUMBNAIL_ORIENTATION;
  const [slidesConfig, setSlidesConfig] = useState(
    thumbnailOrientation === "PORTRAIT" ? { slidesPerView: 8, slidesPerGroup: 8 } : { slidesPerView: 6, slidesPerGroup: 6 }
  );
  
  const [rowItemCountNumber, setRowItemCountNumber] = useState(5);
  const [sliderItemCount, setSliderItemCount] = useState(6);
  const [topics, setTopics] = useState(dataArray?.map(item => item.category_name) || []);
  const [displayedData, setDisplayedData] = useState(activeTopicData?.shows || []);
  const [topRowCards, setTopRowCards] = useState(thumbnailOrientation === "PORTRAIT" ? Math.min(5, numCards) : Math.min(4, numCards));
  const [sliderCards, setSliderCards] = useState(activeTopicData?.shows?.slice(topRowCards) || []);
  const [isLTRActive, setIsLTRActive] = useState(false);
  const [rowItemsClass, setRowItemsClass] = useState("");
  const [isLTR, setIsLTR] = useState(false);
  const [showSlider, setShowSlider] = useState(false);
  const [cardHover, setCardHover] = useState(false);

  const updateSlidesConfig = () => {
    const width = window.innerWidth;
    const config = thumbnailOrientation === "PORTRAIT" 
    ? { slidesPerView: 8, slidesPerGroup: 8 } 
    : { slidesPerView: 6, slidesPerGroup: 6 };

  if (width > 980) setSlidesConfig(config);
  else if (width >= 768) setSlidesConfig({ slidesPerView: 2, slidesPerGroup: 2 });
  else setSlidesConfig({ slidesPerView: 1, slidesPerGroup: 1 });
  };

  useEffect(()=>{
    setActiveTopicData(dataArray?.find(item => item.category_name === activeTopic));
    setNumCards(cardCounts?.[activeTopic] || 0);
  },[activeTopic, dataArray, cardCounts])

  useEffect(() => {
    updateSlidesConfig();
    window.addEventListener('resize', updateSlidesConfig);
    return () => window.removeEventListener('resize', updateSlidesConfig);
  }, []);

  useEffect(() => {
    setTimeout(() => setShowSlider(true), 500);
  }, []);

  const onSlideChange = (swiper) => {
    if (swiper && swiper.params) {
      const lastSlideIndex = swiper.activeIndex + (swiper.params.slidesPerView - 1);
  
      if (lastSlideIndex >= swiper.slides.length) {
        setActiveIndex(swiper.slides.length - 1);
      } else {
        setActiveIndex(lastSlideIndex);
      }
    }
  };

  const handleSwiperInit = (swiperInstance) => {
    if (swiperInstance) {
      swiperRef.current = swiperInstance;
      onSlideChange(swiperInstance);
    }
  };

  useEffect(()=>{
    setTopRowCards(thumbnailOrientation === "PORTRAIT" ? Math.min(5, numCards) : Math.min(4, numCards));
  },[numCards, thumbnailOrientation])

  useEffect(() => {
    if (sliderCards.length > 0 && swiperRef.current) {
      handleSwiperInit(swiperRef.current);
    } else {
      swiperRef.current = null; 
    }
  }, [activeTopic, sliderCards]);

  useEffect(() => {
    const newDisplayedData = activeTopicData?.shows || [];
    const newSliderCards = newDisplayedData?.slice(topRowCards);

    if (isRTL && numCards > rowItemCountNumber) {
      setDisplayedData([...newDisplayedData]?.reverse());
      setSliderCards([...newSliderCards]?.reverse());
    } else {
      setDisplayedData(newDisplayedData);
      setSliderCards(newSliderCards);
    }
  }, [isRTL, activeTopicData, numCards, rowItemCountNumber, topRowCards]);

  useEffect(() => {
    if (windowSize > 980) {
      const newClass = projectInfo?.projectConfig?.config?.WEBSITE_ROW_ITEM_COUNT
        ? rowItemCount(projectInfo?.projectConfig?.config?.WEBSITE_ROW_ITEM_COUNT)
        : "rowCount6"; 
      setRowItemsClass(newClass);
      
      const newCount = parseInt(newClass.replace(/\D/g, ''), 10);
      setRowItemCountNumber(newCount);
      
      if(thumbnailOrientation === "LANDSCAPE"){
        setSliderItemCount(6);
      } else if (thumbnailOrientation === "PORTRAIT"){
        setSliderItemCount(8);
      }
    } else {
      if (windowSize > 767) {
        setRowItemsClass("rowCount3");
        setRowItemCountNumber(3);
      } else if (windowSize > 479) {
        setRowItemsClass("rowCount2");
        setRowItemCountNumber(2);
      } else if (windowSize > 319) {
        setRowItemsClass("rowCount1");
        setRowItemCountNumber(1);
      }
    }
  }, [projectInfo, windowSize, thumbnailOrientation]);

  useEffect(()=>{
    if(isRTL && isLTRActive){
      setTopics(dataArray?.slice().reverse().map(item => item?.category_name) || []);
    } else {
      setTopics(dataArray?.map(item => item?.category_name) || []);
    }
  },[isRTL,isLTRActive,dataArray])

  useEffect(() => {
    setIsLTR(displayedData?.length > rowItemCountNumber);
  }, [displayedData?.length, rowItemCountNumber]);

  const handleSlideChange = (swiper) => {
    if (!swiper) return;

    const tempContainer = document.createElement('div');
    tempContainer.style.visibility = 'hidden';
    tempContainer.style.position = 'absolute';
    tempContainer.style.display = 'flex';  
    tempContainer.style.whiteSpace = 'nowrap';
    
    topics.forEach(topic => {
      const span = document.createElement('span');
      span.textContent = topic;
      span.style.marginRight = '30px';
      span.style.fontSize = '16px';
      tempContainer.appendChild(span);
    });
  
    document.body.appendChild(tempContainer);
    const totalSlidesWidth = tempContainer.offsetWidth;
    document.body.removeChild(tempContainer);
    
    const containerTemp = document.createElement('div');
    containerTemp.style.visibility = 'hidden';
    containerTemp.style.position = 'absolute';
    containerTemp.style.width = '100%';
    document.body.appendChild(containerTemp);
    const containerWidth = containerTemp.offsetWidth;
    document.body.removeChild(containerTemp);
    
    if (isRTL && (totalSlidesWidth + 88 > containerWidth)) {
      setIsLTRActive(true);
    } else {
      setIsLTRActive(false);
    }
  };

  useEffect(() => {
    if (topicsSwiperRef.current) {
      handleSlideChange(topicsSwiperRef.current);
    }
  }, [windowSize, topics, isRTL]);

  const isSingleCard = activeTopicData?.shows?.length === topRowCards + 1;

  const renderCards = () => {
    if (numCards === 0) return null;

    return (
      <>
        {topRowCards > 0 && windowSize > 980 && (
          <div className="topRow">
            {activeTopicData?.shows?.slice(0,topRowCards)?.map((show,index) => (
              <div key={index} className={`topCard ${thumbnailOrientation === "PORTRAIT" ? "portrait" : "landscape"}`}>
                <ShowCard
                    data={show}
                    thumbnailOrientation={thumbnailOrientation}
                    season={false}
                    metaData={true}
                    type={show?.type}
                />
              </div>
            ))}
          </div>
        )}
        {sliderCards.length > 0 && windowSize > 980 && (
          <Swiper
            key={isRTL ? (sliderCards.length > sliderItemCount ? "ltr" : windowSize < 980 ? "rtl" : "rtl") : ""}
            modules={[Navigation]}
            navigation
            spaceBetween={10}
            slidesPerView={slidesConfig.slidesPerView}
            slidesPerGroup={slidesConfig.slidesPerGroup}
            onSlideChange={onSlideChange}
            onSwiper={handleSwiperInit}
            initialSlide={(isRTL && sliderCards.length > sliderItemCount) ? sliderCards.length - 1 : 0} 
            className={`${thumbnailOrientation === "PORTRAIT" ? "portrait" : "landscape"}`}
          >
            {sliderCards?.map((show,index) => (
            <SwiperSlide
                key={index}
                className={!isSingleCard && index === activeIndex ? "translate-last" : ""}
            >
                <ShowCard
                data={show}
                thumbnailOrientation={thumbnailOrientation}
                season={false}
                metaData={true}
                type={show?.type}
                />
            </SwiperSlide>
            ))}
          </Swiper>
        )}
      </>
    );
  };

  const displayedShows = ( isRTL && isLTR ) ? [...displayedData?.slice(0,17)].reverse() : displayedData?.slice(0,17);

  return (
    <div className={
      cardHover
        ? `moreTopicsContainer hideArrow ${thumbnailOrientation === "PORTRAIT" ? "portrait" : "landscape"} ${rowItemsClass} ${(isRTL && isLTR) ? "ltr" : isRTL ? "rtl" : ""}`
        : `moreTopicsContainer ${thumbnailOrientation === "PORTRAIT" ? "portrait" : "landscape"} ${rowItemsClass} ${(isRTL && isLTR) ? "ltr" : isRTL ? "rtl" : ""}`
    }>
      <div className={showSlider ? "showSlider itemsContainer" : "itemsContainer"}>
        <div className="header">
          <div className="categoryNameContainer">
            <div className="content">
              <h1>{data?.category_name ? data?.category_name : <Translate textKey={'more_topics'} />}</h1>
              {activeTopicData?.show_all_needed && (
                <div className="exploreAll">
                  <Link 
                    to={`/category/${activeTopicData?.key}?career=${activeTopicData?.key}`}
                    className="exploreAllLink"
                  >
                    <div className="seeAll">{translateText(languageModal, t, 'explore_all')}</div>
                    <div className="rightArrow">
                      <RightArrow className={`${isRTL && isLTR ? "ltr" : isRTL ? "rtl" : ""}`}/>
                    </div>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <Swiper
          key={isRTL ? isLTRActive ? "ltr" : "rtl" : ""}
          ref={topicsSwiperRef}
          modules={[Navigation]}
          navigation
          spaceBetween={30}
          slidesPerView="auto"
          onSlideChange={handleSlideChange}
          onSwiper={(swiper) => { topicsSwiperRef.current = swiper; }}
          initialSlide={(isRTL && isLTRActive) ? topics.length - 1 : 0}
          className={`topicsList ${isRTL ? isLTRActive ? "ltr" : "rtl" : ""}`}
        >
          {topics.map(topic => (
            <SwiperSlide key={topic}>
              <span
                className={activeTopic === topic ? "active" : ""}
                onClick={() => setActiveTopic(topic)}
              >
                {topic}
              </span>
            </SwiperSlide>
          ))}
        </Swiper>
        
        <div className={`mainCards ${windowSize <= 980 ? "hidden" : ""}`}>
          {renderCards()}
        </div>
        
        {windowSize <= 980 && (
          <Swiper
            key={`${isRTL}-${isLTR}`}
            modules={[Navigation]}
            spaceBetween={1.5}
            slidesPerView={6}
            slidesPerGroup={6}
            navigation={true}
            watchSlidesProgress
            speed={1000}
            loop={false}
            onSwiper={handleSwiperInit} 
            initialSlide={( isRTL && isLTR ) ? displayedShows.length - 1 : 0}
            className={`mobileCards ${thumbnailOrientation==="PORTRAIT" ? "portrait":"landscape"}`}
            breakpoints={{
              320: {
                slidesPerView: thumbnailOrientation == "PORTRAIT" ? 2 : 1,
                spaceBetween: 2,
                slidesPerGroup:thumbnailOrientation == "PORTRAIT" ? 2 : 1,
              },
              360: {
                slidesPerView: thumbnailOrientation == "PORTRAIT" ? 3 : 3,
                spaceBetween: 2,
                slidesPerGroup:thumbnailOrientation == "PORTRAIT" ? 3 : 3,
              },
              480: {
                slidesPerView: thumbnailOrientation == "PORTRAIT" ? 3 : 3,
                spaceBetween: 5,
                slidesPerGroup:thumbnailOrientation == "PORTRAIT" ? 3 : 3,
              },
              640: {
                slidesPerView: thumbnailOrientation == "PORTRAIT" ? 4 : 3,
                spaceBetween: 5,
                slidesPerGroup:thumbnailOrientation == "PORTRAIT" ? 4 : 3,
              },
              768: {
                slidesPerView: thumbnailOrientation == "PORTRAIT" ? 4 : 4,
                spaceBetween: 5,
                slidesPerGroup:thumbnailOrientation == "PORTRAIT" ? 4 : 4,
              },
              980: {
                slidesPerView: projectInfo?.projectConfig?.config?.WEBSITE_ROW_ITEM_COUNT?Number(projectInfo?.projectConfig?.config?.WEBSITE_ROW_ITEM_COUNT):4,
                spaceBetween: 5,
                slidesPerGroup:projectInfo?.projectConfig?.config?.WEBSITE_ROW_ITEM_COUNT?Number(projectInfo?.projectConfig?.config?.WEBSITE_ROW_ITEM_COUNT):4
              }
            }}
          >
            {displayedShows?.map((item,index) => (
              <SwiperSlide 
                key={index} 
                onMouseEnter={()=>setCardHover(true)} 
                onMouseLeave={()=>setCardHover(false)}
                className={`hover ${isRTL ? "rtl" : "ltr"} ${thumbnailOrientation === "PORTRAIT"?`portrait`:`landscape`}`}
              >
                <ShowCard
                  data={item}
                  season={false}
                  metaData={true}
                  type={data?.type}
                  thumbnailOrientation={thumbnailOrientation}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        )}
        
        {activeTopicData?.show_all_needed && (
          <Link 
            to={`/category/${activeTopicData?.key}?career=${activeTopicData?.key}`} 
            className="view-all-mobile"
          >
            <Translate textKey={'view_all'} /> <RightArrow className="arrow-icon" />
          </Link>
        )}
      </div>
    </div>
  );
};

export default MoreTopics;
